import React, { useEffect, useMemo, useState } from 'react';
import '../../css/modal.css';
import { Query } from '../../types/Query';
import { createBookingFromQuery } from '../../api/Booking';
import { extractLatestBookingDraft } from '../../api/BookingDraft';
import BookingComponentTables from './BookingComponentTables';
import {
  BookingComponentsSummary,
  buildBookingComponentsSummary,
  formatDisplayDate,
  formatMoney,
} from '../../utils/bookingComponents';

interface Props {
  query: Query;
  onClose: () => void;
  onCreated: () => void;
}

const AddBookingModal: React.FC<Props> = ({ query, onClose, onCreated }) => {
  const [travelStartDate, setTravelStartDate] = useState<string>('');
  const [travelEndDate, setTravelEndDate] = useState<string>('');
  const [bookingAmount, setBookingAmount] = useState<number>(0);
  const [markup, setMarkup] = useState<number>(0);
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [draftSummary, setDraftSummary] = useState<BookingComponentsSummary | null>(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftError, setDraftError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadDraft = async () => {
      setDraftLoading(true);
      setDraftError('');
      try {
        const draft = await extractLatestBookingDraft(query.id);
        if (cancelled) return;
        const summary = buildBookingComponentsSummary({
          bookingPlan: draft?.booking_plan || draft?.bookingPlan,
          ...draft,
        });
        setDraftSummary(summary);
        if (summary) {
          setTravelStartDate((current) => current || summary.travelStartDate || '');
          setTravelEndDate((current) => current || summary.travelEndDate || '');
          setBookingAmount((current) => (current > 0 ? current : summary.totalAmount));
        }
      } catch (loadError: any) {
        if (cancelled) return;
        setDraftError(loadError?.message || 'Failed to load booking draft');
      } finally {
        if (!cancelled) setDraftLoading(false);
      }
    };

    void loadDraft();

    return () => {
      cancelled = true;
    };
  }, [query.id]);

  const totalBookingAmount = useMemo(() => Math.max(0, Number(bookingAmount || 0) + Number(markup || 0)), [bookingAmount, markup]);

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError('');
      const totalPrice = totalBookingAmount;
      await createBookingFromQuery(query.id, {
        customerId: query.customerId || query.customer?.id,
        travelStartDate,
        travelEndDate,
        totalPrice,
        notes,
        status: 'BOOKING',
        extras: {
          bookingAmount: Number(bookingAmount || 0),
          markup: Number(markup || 0),
          notes,
        },
      });
      onCreated();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to create booking');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg add-booking-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Booking</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {draftLoading && (
            <div style={{ marginBottom: 12, fontSize: 13, color: '#6b7280' }}>
              Loading booking draft…
            </div>
          )}
          {draftError && (
            <div style={{ marginBottom: 12, color: '#b91c1c', fontSize: 13 }}>
              {draftError}
            </div>
          )}
          {draftSummary && (
            <div className="booking-draft-summary">
              <div className="booking-draft-summary__header">
                <div>
                  <div className="booking-draft-summary__title">Booking Draft Summary</div>
                  <div className="booking-draft-summary__subtitle">Latest quoted trip details</div>
                </div>
                <div className="booking-draft-summary__amount">
                  {formatMoney(draftSummary.totalAmount)}
                </div>
              </div>

              <div className="booking-draft-summary__facts">
                <div className="booking-draft-summary__fact">
                  <span className="booking-draft-summary__label">Travel start</span>
                  <strong>{formatDisplayDate(draftSummary.travelStartDate)}</strong>
                </div>
                <div className="booking-draft-summary__fact">
                  <span className="booking-draft-summary__label">Travel end</span>
                  <strong>{formatDisplayDate(draftSummary.travelEndDate)}</strong>
                </div>
                <div className="booking-draft-summary__fact">
                  <span className="booking-draft-summary__label">Hotels</span>
                  <strong>{draftSummary.hotels.length || '—'}</strong>
                </div>
                <div className="booking-draft-summary__fact">
                  <span className="booking-draft-summary__label">Flights</span>
                  <strong>{draftSummary.flights.length || '—'}</strong>
                </div>
                <div className="booking-draft-summary__fact">
                  <span className="booking-draft-summary__label">Cab</span>
                  <strong>{draftSummary.cabLine || '—'}</strong>
                </div>
                <div className="booking-draft-summary__fact">
                  <span className="booking-draft-summary__label">Travel window</span>
                  <strong>
                    {draftSummary.travelStartDate
                      ? `${formatDisplayDate(draftSummary.travelStartDate)} to ${formatDisplayDate(draftSummary.travelEndDate)}`
                      : '—'}
                  </strong>
                </div>
              </div>

              <BookingComponentTables
                summary={draftSummary}
                emptyText="No component details available"
                showEmptyState={false}
              />
            </div>
          )}
          <div style={{ marginBottom: 8, fontSize: 14, color: '#374151' }}>
            <strong>Customer:</strong> {query.customer?.name || '—'} · <strong>Type:</strong> {query.type} · <strong>Location:</strong> {query.location}
          </div>
          <label>
            Travel Start Date
            <input
              aria-label="Travel Start Date"
              type="date"
              value={travelStartDate}
              onChange={e => setTravelStartDate(e.target.value)}
            />
          </label>
          <label>
            Travel End Date
            <input
              aria-label="Travel End Date"
              type="date"
              value={travelEndDate}
              onChange={e => setTravelEndDate(e.target.value)}
            />
          </label>
          <label>
            Booking Amount (₹)
            <input
              aria-label="Booking Amount (₹)"
              type="number"
              min="0"
              step="0.01"
              value={bookingAmount}
              onChange={e => setBookingAmount(Number(e.target.value || 0))}
            />
          </label>
          <label>
            Markup (₹)
            <input
              type="number"
              min="0"
              step="0.01"
              value={markup}
              onChange={e => setMarkup(Number(e.target.value || 0))}
            />
          </label>
          <label>
            Notes
            <textarea value={notes} onChange={e => setNotes(e.target.value)} />
          </label>
          {error && <div style={{ color: '#b91c1c', fontSize: 12 }}>{error}</div>}
        </div>
        <div className="modal-actions">
          <button type="button" className="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : 'Create Booking'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddBookingModal;
